<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-file-search</v-icon>
            External RAPS Files Analysis
          </v-card-title>
          <v-card-subtitle>
            Upload a generated RAPS file (.txt or .RPT) and its return file (.RPT) for analysis
          </v-card-subtitle>
          
          <v-card-text>
            <v-form ref="uploadForm" v-model="formValid">
              <v-row>
                <v-col cols="12" md="6">
                  <v-file-input
                    v-model="rapsFile"
                    label="Generated RAPS File (.txt or .RPT)"
                    accept=".txt,.rpt"
                    prepend-icon="mdi-file-document"
                    show-size
                    :rules="[rules.required]"
                    :disabled="analyzing"
                  ></v-file-input>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-file-input
                    v-model="returnFile"
                    label="Return File (.RPT)"
                    accept=".rpt,.txt"
                    prepend-icon="mdi-file-return"
                    show-size
                    :rules="[rules.required]"
                    :disabled="analyzing"
                  ></v-file-input>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12">
                  <v-btn
                    color="primary"
                    large
                    @click="analyzeFiles"
                    :loading="analyzing"
                    :disabled="!formValid || !rapsFile || !returnFile || analyzing"
                    block
                  >
                    <v-icon left>mdi-magnify</v-icon>
                    Analyze Files
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Analysis Results -->
    <v-row v-if="analysisResults && analysisResults.length > 0">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-chart-box</v-icon>
            Analysis Results
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              @click="exportToExcel"
              :loading="exporting"
              :disabled="exporting"
            >
              <v-icon left>mdi-file-excel</v-icon>
              Export to Excel
            </v-btn>
          </v-card-title>
          
          <v-card-text>
            <!-- Summary -->
            <v-row class="mb-4">
              <v-col cols="12" md="3">
                <v-card color="info" dark>
                  <v-card-text>
                    <div class="text-h6">{{ summary.totalRapsRecords }}</div>
                    <div class="text-caption">Total RAPS Records</div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="12" md="3">
                <v-card color="success" dark>
                  <v-card-text>
                    <div class="text-h6">{{ summary.accepted }}</div>
                    <div class="text-caption">Accepted</div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="12" md="3">
                <v-card color="error" dark>
                  <v-card-text>
                    <div class="text-h6">{{ summary.errors }}</div>
                    <div class="text-caption">With Errors</div>
                  </v-card-text>
                </v-card>
              </v-col>
              
            </v-row>
            
            <!-- Filters -->
            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-select
                  v-model="statusFilter"
                  :items="statusOptions"
                  label="Filter by Status"
                  clearable
                  dense
                  outlined
                ></v-select>
              </v-col>
              
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="searchFilter"
                  label="Search by Patient Control No or Member ID"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  dense
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
            
            <!-- Results Table -->
            <v-data-table
              :headers="headers"
              :items="filteredResults"
              :items-per-page="25"
              :search="searchFilter"
              class="elevation-1"
              :loading="analyzing"
            >
              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="getStatusColor(item.status)"
                  dark
                  small
                >
                  {{ getStatusText(item.status) }}
                </v-chip>
              </template>
              
              <template v-slot:item.errorCode="{ item }">
                <span v-if="item.errorCode" class="error--text">
                  {{ item.errorCode }}
                </span>
                <span v-else class="grey--text">-</span>
              </template>
              
              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon
                  small
                  @click="viewDetails(item)"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Details Dialog -->
    <v-dialog v-model="showDetailsDialog" max-width="800">
      <v-card v-if="selectedResult">
        <v-card-title>
          Record Details
        </v-card-title>
        <v-card-text>
          <v-simple-table>
            <tbody>
              <tr>
                <td><strong>SEQ No:</strong></td>
                <td>{{ selectedResult.seqNo }}</td>
              </tr>
              <tr>
                <td><strong>Patient Control No:</strong></td>
                <td>{{ selectedResult.patientControlNo }}</td>
              </tr>
              <tr>
                <td><strong>Member ID:</strong></td>
                <td>{{ selectedResult.memberId }}</td>
              </tr>
              <tr>
                <td><strong>Patient DOB:</strong></td>
                <td>{{ selectedResult.patientDob }}</td>
              </tr>
              <tr>
                <td><strong>Status:</strong></td>
                <td>
                  <v-chip
                    :color="getStatusColor(selectedResult.status)"
                    dark
                    small
                  >
                    {{ getStatusText(selectedResult.status) }}
                  </v-chip>
                </td>
              </tr>
              <tr v-if="selectedResult.errorCode">
                <td><strong>Error Code(s):</strong></td>
                <td class="error--text">{{ selectedResult.errorCode }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDetailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Snackbar for messages -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'RAPSAnalyzeExternal',
  data() {
    return {
      formValid: false,
      rapsFile: null,
      returnFile: null,
      analyzing: false,
      exporting: false,
      analysisResults: [],
      summary: {},
      fileId: null,
      statusFilter: null,
      searchFilter: '',
      selectedResult: null,
      showDetailsDialog: false,
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 5000
      },
      rules: {
        required: value => !!value || 'Field required'
      },
      statusOptions: [
        { text: 'Accepted', value: 'accepted' },
        { text: 'With Errors', value: 'error' }
      ],
      headers: [
        { text: 'SEQ No', value: 'seqNo', sortable: true },
        { text: 'Patient Control No', value: 'patientControlNo', sortable: true },
        { text: 'Member ID', value: 'memberId', sortable: true },
        { text: 'Patient DOB', value: 'patientDob', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Error Code(s)', value: 'errorCode', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' }
      ]
    };
  },
  computed: {
    filteredResults() {
      let results = this.analysisResults;
      
      if (this.statusFilter) {
        results = results.filter(r => r.status === this.statusFilter);
      }
      
      if (this.searchFilter) {
        const search = this.searchFilter.toLowerCase();
        results = results.filter(r => 
          String(r.patientControlNo).toLowerCase().includes(search) ||
          String(r.memberId).toLowerCase().includes(search)
        );
      }
      
      return results;
    }
  },
  methods: {
    async analyzeFiles() {
      if (!this.rapsFile || !this.returnFile) {
        this.showSnackbar('Please select both files', 'error');
        return;
      }
      
      this.analyzing = true;
      
      const formData = new FormData();
      formData.append('rapsFile', this.rapsFile);
      formData.append('returnFile', this.returnFile);
      
      try {
        const response = await axios.post('/api/raps/analyze-external', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        this.analysisResults = response.data.results;
        this.summary = response.data.summary;
        this.fileId = response.data.fileId;
        
        this.showSnackbar(
          `Analysis completed! ${this.summary.accepted} accepted, ${this.summary.errors} with errors, ${this.summary.notFound} not found`,
          'success'
        );
      } catch (error) {
        console.error('Error analyzing files:', error);
        const errorMessage = (error.response && error.response.data && error.response.data.error) 
          ? error.response.data.error 
          : 'Error analyzing files';
        this.showSnackbar(errorMessage, 'error');
      } finally {
        this.analyzing = false;
      }
    },
    
    async exportToExcel() {
      if (!this.filteredResults || this.filteredResults.length === 0) {
        this.showSnackbar('No data to export', 'warning');
        return;
      }
      
      this.exporting = true;
      
      try {
        const response = await axios.post(
          '/api/raps/export-external-analysis',
          {
            results: this.filteredResults,
            fileId: this.fileId,
            summary: this.summary
          },
          {
            responseType: 'blob'
          }
        );
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const fileName = `RAPS_Analysis_${this.fileId || 'External'}_${new Date().toISOString().split('T')[0]}.xlsx`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        this.showSnackbar('Excel file exported successfully', 'success');
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        console.error('Error response:', error.response);
        
        let errorMessage = 'Error exporting to Excel';
        if (error.response) {
          if (error.response.data) {
            if (typeof error.response.data === 'string') {
              errorMessage = error.response.data;
            } else if (error.response.data.error) {
              errorMessage = error.response.data.error;
            }
          } else {
            errorMessage = `Error ${error.response.status}: ${error.response.statusText}`;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.showSnackbar(errorMessage, 'error');
      } finally {
        this.exporting = false;
      }
    },
    
    viewDetails(item) {
      this.selectedResult = item;
      this.showDetailsDialog = true;
    },
    
    getStatusColor(status) {
      const colors = {
        accepted: 'success',
        error: 'error'
      };
      return colors[status] || 'grey';
    },
    
    getStatusText(status) {
      const texts = {
        accepted: 'Accepted',
        error: 'With Errors'
      };
      return texts[status] || 'Unknown';
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color,
        timeout: 5000
      };
    }
  }
};
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}
</style>
